/**
 * DEV ONLY — Page de test rapide des endpoints Sizing API (BAB-2505).
 * À retirer ou protéger avant mise en production.
 */

import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import config from "@/config";
import { Badge } from "@babackapp/ui/components/badge";
import { Button } from "@babackapp/ui/components/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@babackapp/ui/components/card";
import { Input } from "@babackapp/ui/components/input";
import { Label } from "@babackapp/ui/components/label";
import { useState } from "react";

// ─── Helpers ────────────────────────────────────────────────────────────────

type HttpMethod = "GET" | "PATCH";

// window.interceptedFetch est injecté par @babackapp/auth-client/setup et ajoute
// Authorization: Bearer <token>. Il attend un Request object en premier argument
// (comme openapi-fetch), pas un couple (url_string, init).
type InterceptedFetch = (req: Request) => Promise<Response>;
const interceptedFetch =
  (window as Window & { interceptedFetch?: InterceptedFetch })
    .interceptedFetch ?? ((req: Request) => window.fetch(req));

async function callSizingApi(
  method: HttpMethod,
  shopId: string,
  path: string,
  body?: Record<string, unknown>,
): Promise<unknown> {
  const url = `${config.api.url}/shops/${shopId}/sizing${path}`;
  const req = new Request(url, {
    method,
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const res = await interceptedFetch(req);
  if (!res.ok) {
    const err = await res.json().catch(() => ({ statusCode: res.status }));
    throw err;
  }
  return res.json();
}

// ─── Section component ────────────────────────────────────────────────────────

interface SectionProps {
  title: string;
  method: HttpMethod;
  description: string;
  children?: React.ReactNode;
  onRun: () => Promise<unknown>;
}

function Section({
  title,
  method,
  description,
  children,
  onRun,
}: SectionProps) {
  const [result, setResult] = useState<unknown>(undefined);
  const [error, setError] = useState<unknown>(undefined);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    setError(undefined);
    setResult(undefined);
    try {
      const data = await onRun();
      setResult(data);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Badge
            variant={method === "GET" ? "default" : "secondary"}
            className="font-mono text-xs"
          >
            {method}
          </Badge>
          {title}
        </CardTitle>
        <p className="text-sm text-muted-foreground font-mono">{description}</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {children && <div className="grid grid-cols-2 gap-3">{children}</div>}
        <Button onClick={run} disabled={loading} size="sm">
          {loading ? "Chargement…" : "Exécuter"}
        </Button>
        {result !== undefined && (
          <pre className="text-xs bg-muted rounded-md p-3 overflow-auto max-h-80 whitespace-pre-wrap">
            {JSON.stringify(result, null, 2)}
          </pre>
        )}
        {error !== undefined && (
          <pre className="text-xs bg-destructive/10 text-destructive rounded-md p-3 overflow-auto max-h-48 whitespace-pre-wrap">
            {JSON.stringify(error, null, 2)}
          </pre>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Field helpers ────────────────────────────────────────────────────────────

function Field({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <Label className="text-xs">{label}</Label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-8 text-sm font-mono"
      />
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function SizingDebugPage() {
  const { data: shop } = useCurrentShopSuspense();
  const shopId = shop.id;

  // § 1 — Recommendation
  const [recProductId, setRecProductId] = useState("");

  // § 2 — Diagnostic
  const [diagFrom, setDiagFrom] = useState("");
  const [diagTo, setDiagTo] = useState("");

  // § 3 — Impact
  const [impactFrom, setImpactFrom] = useState("");
  const [impactTo, setImpactTo] = useState("");
  const [impactProductId, setImpactProductId] = useState("");

  // § 5 — Update config
  const [cfgThreshold, setCfgThreshold] = useState("");
  const [cfgMinSample, setCfgMinSample] = useState("");
  const [cfgActivate, setCfgActivate] = useState("");

  const buildQuery = (params: Record<string, string | undefined>): string => {
    const qs = Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== "")
      .map(([k, v]) => `${k}=${encodeURIComponent(v!)}`)
      .join("&");
    return qs ? `?${qs}` : "";
  };

  return (
    <div className="py-10 px-10 space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-semibold">Sizing API — Debug</h1>
          <Badge variant="outline" className="text-xs">
            DEV
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground">
          Shop&nbsp;:&nbsp;
          <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">
            {shopId}
          </span>
        </p>
      </div>

      {/* 1. Recommendation */}
      <Section
        title="Recommandation produit"
        method="GET"
        description={`/shops/{shopId}/sizing/products/{productId}/recommendation`}
        onRun={() =>
          callSizingApi(
            "GET",
            shopId,
            `/products/${recProductId}/recommendation`,
          )
        }
      >
        <Field
          label="productId *"
          value={recProductId}
          onChange={setRecProductId}
          placeholder="ex: 123456789"
        />
      </Section>

      {/* 2. Diagnostic */}
      <Section
        title="Diagnostic sizing"
        method="GET"
        description="/shops/{shopId}/sizing/diagnostic"
        onRun={() => {
          const qs = buildQuery({
            fromTimestamp: diagFrom || undefined,
            toTimestamp: diagTo || undefined,
          });
          return callSizingApi("GET", shopId, `/diagnostic${qs}`);
        }}
      >
        <Field
          label="fromTimestamp (ms)"
          value={diagFrom}
          onChange={setDiagFrom}
          placeholder="ex: 1700000000000"
          type="number"
        />
        <Field
          label="toTimestamp (ms)"
          value={diagTo}
          onChange={setDiagTo}
          placeholder="ex: 1710000000000"
          type="number"
        />
      </Section>

      {/* 3. Impact */}
      <Section
        title="Impact sizing (avant/après widget)"
        method="GET"
        description="/shops/{shopId}/sizing/impact"
        onRun={() => {
          const qs = buildQuery({
            fromTimestamp: impactFrom || undefined,
            toTimestamp: impactTo || undefined,
            productId: impactProductId || undefined,
          });
          return callSizingApi("GET", shopId, `/impact${qs}`);
        }}
      >
        <Field
          label="fromTimestamp (ms)"
          value={impactFrom}
          onChange={setImpactFrom}
          placeholder="laisser vide = 3 derniers mois"
          type="number"
        />
        <Field
          label="toTimestamp (ms)"
          value={impactTo}
          onChange={setImpactTo}
          placeholder="laisser vide = maintenant"
          type="number"
        />
        <Field
          label="productId (optionnel)"
          value={impactProductId}
          onChange={setImpactProductId}
          placeholder="filtrer sur un produit"
        />
      </Section>

      {/* 4. Get config */}
      <Section
        title="Configuration actuelle"
        method="GET"
        description="/shops/{shopId}/sizing/config"
        onRun={() => callSizingApi("GET", shopId, "/config")}
      />

      {/* 5. Update config */}
      <Section
        title="Mettre à jour la configuration"
        method="PATCH"
        description="/shops/{shopId}/sizing/config"
        onRun={() => {
          const body: Record<string, unknown> = {};
          if (cfgThreshold !== "") body.threshold = parseFloat(cfgThreshold);
          if (cfgMinSample !== "")
            body.minSampleSize = parseInt(cfgMinSample, 10);
          if (cfgActivate === "true") body.activateWidget = true;
          return callSizingApi("PATCH", shopId, "/config", body);
        }}
      >
        <Field
          label="threshold (0–1)"
          value={cfgThreshold}
          onChange={setCfgThreshold}
          placeholder="ex: 0.6"
          type="number"
        />
        <Field
          label="minSampleSize"
          value={cfgMinSample}
          onChange={setCfgMinSample}
          placeholder="ex: 15"
          type="number"
        />
        <Field
          label='activateWidget ("true" pour activer)'
          value={cfgActivate}
          onChange={setCfgActivate}
          placeholder='laisser vide ou "true"'
        />
      </Section>
    </div>
  );
}
